/**
 * Field Detail Sheet
 * Sheet for editing existing field with tabs matching reference:
 * - Schema (field key, type, constraints, triggers)
 * - Field (readonly, required, hidden, note)
 * - Interface (interface type, options)
 * - Display (coming soon)
 * - Validation (coming soon)
 * - Conditions (coming soon)
 */

import { useState, useEffect } from 'react';
import { Save, Loader2, Trash2, AlertCircle, Plus, ArrowRight, Link2, ExternalLink, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useField, useUpdateField, useDeleteField } from '@/hooks/useFieldsQuery';
import { useRelation, useUpdateRelation, useRelationM2M } from '@/hooks/useRelations';
import { toast } from 'sonner';
import { FolderSelect } from '@/components/FolderSelect';
import { DisplayTemplateInput } from '@/components/interfaces/DisplayTemplateInput';

// On Delete/Update actions for relational triggers
const ON_DELETE_ACTIONS = [
    { value: 'NO ACTION', label: 'No Action', description: 'Do nothing when related item is deleted' },
    { value: 'SET NULL', label: 'Set NULL', description: 'Set this field to null' },
    { value: 'SET DEFAULT', label: 'Set Default', description: 'Set this field to its default value' },
    { value: 'CASCADE', label: 'Cascade', description: 'Delete this item as well' },
    { value: 'RESTRICT', label: 'Restrict', description: 'Prevent deletion of related item if this item exists' },
] as const;

interface FieldDetailSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    collection: string;
    fieldName: string;
}

// Available interfaces - Limited set matching reference behavior
// These are the main interfaces that make sense for field editing
const INTERFACES = [
    { value: 'input', label: 'Input' },
    { value: 'input-url', label: 'URL' },
    { value: 'input-multiline', label: 'Textarea' },
    { value: 'input-rich-text-md', label: 'Markdown' },
    { value: 'select-dropdown', label: 'Dropdown' },
    { value: 'select-multiple-dropdown', label: 'Dropdown (Multiple)' },
    { value: 'select-color', label: 'Color' },
    { value: 'select-icon', label: 'Icon' },
    { value: 'select-radio', label: 'Radio Buttons' },
    { value: 'select-dropdown-m2o', label: 'Many to One' },
    { value: 'list-m2m', label: 'Many to Many' },
    { value: 'datetime', label: 'DateTime' },
    { value: 'boolean', label: 'Checkbox' },
    { value: 'file', label: 'File' },
    { value: 'file-image', label: 'Image' },
    { value: 'presentation-links', label: 'Button Links' },
] as const;

// Markdown toolbar options
const MARKDOWN_TOOLBAR_OPTIONS = [
    { value: 'heading', label: 'Heading' },
    { value: 'bold', label: 'Bold' },
    { value: 'italic', label: 'Italic' },
    { value: 'strikethrough', label: 'Strikethrough' },
    { value: 'bullist', label: 'Bullet List' },
    { value: 'numlist', label: 'Numbered List' },
    { value: 'blockquote', label: 'Blockquote' },
    { value: 'code', label: 'Code' },
    { value: 'link', label: 'Link' },
    { value: 'table', label: 'Table' },
    { value: 'image', label: 'Image' },
] as const;

// Button type options for presentation-links
const BUTTON_TYPE_OPTIONS = [
    { value: 'primary', label: 'Primary' },
    { value: 'normal', label: 'Normal' },
    { value: 'info', label: 'Info' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' },
    { value: 'danger', label: 'Danger' },
] as const;

// Link item for presentation-links
interface LinkItem {
    label: string;
    icon: string;
    type: string;
    url: string;
}

// Width options
const WIDTH_OPTIONS = [
    { value: 'half', label: 'Half Width' },
    { value: 'half-left', label: 'Half (Left)' },
    { value: 'half-right', label: 'Half (Right)' },
    { value: 'full', label: 'Full Width' },
    { value: 'fill', label: 'Fill' },
] as const;

// Type options
const TYPE_OPTIONS = [
    { value: 'string', label: 'String' },
    { value: 'text', label: 'Text' },
    { value: 'integer', label: 'Integer' },
    { value: 'bigInteger', label: 'Big Integer' },
    { value: 'float', label: 'Float' },
    { value: 'decimal', label: 'Decimal' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'timestamp', label: 'Timestamp' },
    { value: 'datetime', label: 'DateTime' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'json', label: 'JSON' },
    { value: 'uuid', label: 'UUID' },
    { value: 'hash', label: 'Hash' },
    { value: 'csv', label: 'CSV' },
] as const;

// On Create trigger options
const ON_CREATE_OPTIONS = [
    { value: '_none', label: 'Do nothing' },
    { value: 'uuid', label: 'Generate and save UUID' },
    { value: 'user-created', label: 'Save current user ID' },
    { value: 'role-created', label: 'Save current user role' },
    { value: 'date-created', label: 'Save current date/time' },
] as const;

// On Update trigger options  
const ON_UPDATE_OPTIONS = [
    { value: '_none', label: 'Do nothing' },
    { value: 'user-updated', label: 'Save current user ID' },
    { value: 'role-updated', label: 'Save current user role' },
    { value: 'date-updated', label: 'Save current date/time' },
] as const;

// Datetime-specific trigger options (only do nothing and save date/time)
const DATETIME_ON_CREATE_OPTIONS = [
    { value: '_none', label: 'Do nothing' },
    { value: 'date-created', label: 'Save current date/time' },
] as const;

const DATETIME_ON_UPDATE_OPTIONS = [
    { value: '_none', label: 'Do nothing' },
    { value: 'date-updated', label: 'Save current date/time' },
] as const;

// Interfaces that Markdown can be changed to
const MARKDOWN_ALLOWED_INTERFACES = [
    { value: 'input-rich-text-md', label: 'Markdown' },
    { value: 'input', label: 'Input' },
    { value: 'input-multiline', label: 'Textarea' },
] as const;

type WidthType = 'half' | 'half-left' | 'half-right' | 'full' | 'fill';

interface FieldTranslation {
    language: string;
    translation: string;
}

interface FieldMeta {
    interface: string;
    note: string;
    hidden: boolean;
    readonly: boolean;
    required: boolean;
    width: WidthType;
    special: string[];
    options: Record<string, unknown>;
    translations: FieldTranslation[];
}

interface FieldSchema {
    is_nullable: boolean;
    is_unique: boolean;
    is_indexed: boolean;
    default_value: string;
    max_length: number | null;
    numeric_precision: number | null;
    numeric_scale: number | null;
}

// Store original values to compare for dirty checking
interface OriginalValues {
    meta: FieldMeta;
    schema: FieldSchema;
}

export function FieldDetailSheet({ open, onOpenChange, collection, fieldName }: FieldDetailSheetProps) {
    const { data: field, isLoading } = useField(collection, fieldName);
    const updateField = useUpdateField(collection);
    const deleteField = useDeleteField(collection);

    // Relation data for M2O fields (direct relation lookup)
    const { data: relation, isLoading: relationLoading } = useRelation(collection, fieldName);
    const updateRelation = useUpdateRelation(collection, fieldName);

    // M2M relation data (lookup via all relations since M2M relation is on junction table)
    const m2mRelationData = useRelationM2M(collection, fieldName);

    const [currentTab, setCurrentTab] = useState('schema');
    const [fieldType, setFieldType] = useState('string');
    const [meta, setMeta] = useState<FieldMeta>({
        interface: 'input',
        note: '',
        hidden: false,
        readonly: false,
        required: false,
        width: 'full',
        special: [],
        options: {},
        translations: [],
    });
    const [schema, setSchema] = useState<FieldSchema>({
        is_nullable: true,
        is_unique: false,
        is_indexed: false,
        default_value: '',
        max_length: null,
        numeric_precision: null,
        numeric_scale: null,
    });
    const [originalValues, setOriginalValues] = useState<OriginalValues | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Relation state for M2O fields
    const [onDeleteAction, setOnDeleteAction] = useState<string>('SET NULL');
    const [originalOnDelete, setOriginalOnDelete] = useState<string>('SET NULL');

    // Relation state for M2M fields
    const [m2mOnDeselectAction, setM2mOnDeselectAction] = useState<string>('nullify');
    const [m2mOnDeleteCurrent, setM2mOnDeleteCurrent] = useState<string>('SET NULL');
    const [m2mOnDeleteRelated, setM2mOnDeleteRelated] = useState<string>('SET NULL');
    const [originalM2mOnDeselect, setOriginalM2mOnDeselect] = useState<string>('nullify');
    const [originalM2mOnDeleteCurrent, setOriginalM2mOnDeleteCurrent] = useState<string>('SET NULL');
    const [originalM2mOnDeleteRelated, setOriginalM2mOnDeleteRelated] = useState<string>('SET NULL');

    // Check if this is a M2M relational field (check first since it's more specific)
    const isM2MField = meta.interface === 'list-m2m' ||
        (meta.special && meta.special.includes('m2m'));

    // Check if this is a M2O relational field
    const isM2OField = !isM2MField && (
        meta.interface === 'select-dropdown-m2o' ||
        (meta.special && meta.special.includes('m2o')) ||
        (relation && relation.related_collection)
    );

    // Combined check for any relational field (for showing Relationship tab)
    const isRelationalField = isM2OField || isM2MField;

    // Initialize from field data
    useEffect(() => {
        if (field) {
            setFieldType(field.type ?? 'string');
            const initialMeta: FieldMeta = {
                interface: field.meta?.interface ?? 'input',
                note: field.meta?.note ?? '',
                hidden: field.meta?.hidden ?? false,
                readonly: field.meta?.readonly ?? false,
                required: field.meta?.required ?? false,
                width: (field.meta?.width ?? 'full') as WidthType,
                special: field.meta?.special ?? [],
                options: field.meta?.options ?? {},
                translations: (field.meta?.translations as FieldTranslation[]) ?? [],
            };
            const initialSchema: FieldSchema = {
                is_nullable: field.schema?.is_nullable ?? true,
                is_unique: field.schema?.is_unique ?? false,
                is_indexed: field.schema?.is_indexed ?? false,
                default_value: String(field.schema?.default_value ?? ''),
                max_length: field.schema?.max_length ?? null,
                numeric_precision: field.schema?.numeric_precision ?? null,
                numeric_scale: field.schema?.numeric_scale ?? null,
            };
            setMeta(initialMeta);
            setSchema(initialSchema);
            setOriginalValues({ meta: initialMeta, schema: initialSchema });
            setIsDirty(false);
        }
    }, [field]);

    // Initialize relation data for M2O
    useEffect(() => {
        if (relation) {
            const onDelete = relation.schema?.on_delete || 'SET NULL';
            setOnDeleteAction(onDelete);
            setOriginalOnDelete(onDelete);
        }
    }, [relation]);

    // Initialize relation data for M2M
    useEffect(() => {
        if (m2mRelationData.data) {
            const onDeselect = m2mRelationData.data.meta?.one_deselect_action || 'nullify';
            const onDeleteCurrent = m2mRelationData.data.schema?.on_delete || 'SET NULL';
            setM2mOnDeselectAction(onDeselect);
            setM2mOnDeleteCurrent(onDeleteCurrent);
            setOriginalM2mOnDeselect(onDeselect);
            setOriginalM2mOnDeleteCurrent(onDeleteCurrent);
        }
        if (m2mRelationData.relatedRelation) {
            const onDeleteRelated = m2mRelationData.relatedRelation.schema?.on_delete || 'SET NULL';
            setM2mOnDeleteRelated(onDeleteRelated);
            setOriginalM2mOnDeleteRelated(onDeleteRelated);
        }
    }, [m2mRelationData.data, m2mRelationData.relatedRelation]);

    const updateMetaField = <K extends keyof FieldMeta>(key: K, value: FieldMeta[K]) => {
        setMeta((prev) => ({ ...prev, [key]: value }));
        setIsDirty(true);
    };

    const updateSchemaField = <K extends keyof FieldSchema>(key: K, value: FieldSchema[K]) => {
        setSchema((prev) => ({ ...prev, [key]: value }));
        setIsDirty(true);
    };

    // Export choices to CSV
    const exportChoicesToCSV = () => {
        const choices = (meta.options?.choices as Array<{ text: string; value: string }>) || [];
        if (choices.length === 0) {
            toast.error('No choices to export');
            return;
        }

        // Create CSV content
        const csvContent = 'text,value\n' + choices.map(c =>
            `"${c.text.replace(/"/g, '""')}","${c.value.replace(/"/g, '""')}"`
        ).join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${field?.field || 'field'}_choices.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Choices exported to CSV');
    };

    // Import choices from CSV
    const importChoicesFromCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Reset the input so the same file can be selected again
        event.target.value = '';

        if (!file.name.endsWith('.csv')) {
            toast.error('Please select a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.split('\n').map(line => line.trim()).filter(line => line);

                if (lines.length < 2) {
                    toast.error('CSV file must contain at least a header and one data row');
                    return;
                }

                // Parse CSV (simple parser for text,value format)
                const parseCSVLine = (line: string): string[] => {
                    const result: string[] = [];
                    let current = '';
                    let inQuotes = false;

                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        const nextChar = line[i + 1];

                        if (char === '"') {
                            if (inQuotes && nextChar === '"') {
                                current += '"';
                                i++; // Skip next quote
                            } else {
                                inQuotes = !inQuotes;
                            }
                        } else if (char === ',' && !inQuotes) {
                            result.push(current);
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    result.push(current);
                    return result;
                };

                // Skip header
                const dataLines = lines.slice(1);
                const importedChoices: Array<{ text: string; value: string }> = [];

                for (const line of dataLines) {
                    const [text, value] = parseCSVLine(line);
                    if (text !== undefined && value !== undefined) {
                        importedChoices.push({
                            text: text.trim(),
                            value: value.trim()
                        });
                    }
                }

                if (importedChoices.length === 0) {
                    toast.error('No valid choices found in CSV file');
                    return;
                }

                updateMetaField('options', { ...meta.options, choices: importedChoices });
                toast.success(`Imported ${importedChoices.length} choice${importedChoices.length !== 1 ? 's' : ''}`);
            } catch (error) {
                console.error('CSV import error:', error);
                toast.error('Failed to parse CSV file');
            }
        };

        reader.onerror = () => {
            toast.error('Failed to read CSV file');
        };

        reader.readAsText(file);
    };

    // Get special value for triggers
    const getOnCreateSpecial = () => {
        const specials = meta.special || [];
        if (specials.includes('uuid')) return 'uuid';
        if (specials.includes('user-created')) return 'user-created';
        if (specials.includes('role-created')) return 'role-created';
        if (specials.includes('date-created')) return 'date-created';
        return '_none';
    };

    const getOnUpdateSpecial = () => {
        const specials = meta.special || [];
        if (specials.includes('user-updated')) return 'user-updated';
        if (specials.includes('role-updated')) return 'role-updated';
        if (specials.includes('date-updated')) return 'date-updated';
        return '_none';
    };

    const setOnCreateSpecial = (value: string) => {
        const createSpecials = ['uuid', 'user-created', 'role-created', 'date-created'];
        let newSpecials = (meta.special || []).filter(s => !createSpecials.includes(s));
        if (value && value !== '_none') newSpecials.push(value);
        updateMetaField('special', newSpecials);
    };

    const setOnUpdateSpecial = (value: string) => {
        const updateSpecials = ['user-updated', 'role-updated', 'date-updated'];
        let newSpecials = (meta.special || []).filter(s => !updateSpecials.includes(s));
        if (value && value !== '_none') newSpecials.push(value);
        updateMetaField('special', newSpecials);
    };

    // Build payload with only changed values (like reference implementation)
    const buildChangedPayload = () => {
        const payload: { meta?: Record<string, unknown>; schema?: Record<string, unknown> } = {};

        if (originalValues) {
            // Check meta changes
            const metaChanges: Record<string, unknown> = {};
            if (meta.interface !== originalValues.meta.interface) metaChanges.interface = meta.interface;
            if (meta.note !== originalValues.meta.note) metaChanges.note = meta.note || null;
            if (meta.hidden !== originalValues.meta.hidden) metaChanges.hidden = meta.hidden;
            if (meta.readonly !== originalValues.meta.readonly) metaChanges.readonly = meta.readonly;
            if (meta.required !== originalValues.meta.required) metaChanges.required = meta.required;
            if (meta.width !== originalValues.meta.width) metaChanges.width = meta.width;
            if (JSON.stringify(meta.special) !== JSON.stringify(originalValues.meta.special)) {
                metaChanges.special = meta.special.length > 0 ? meta.special : null;
            }
            if (JSON.stringify(meta.options) !== JSON.stringify(originalValues.meta.options)) {
                metaChanges.options = Object.keys(meta.options).length > 0 ? meta.options : null;
            }
            if (JSON.stringify(meta.translations) !== JSON.stringify(originalValues.meta.translations)) {
                metaChanges.translations = meta.translations.length > 0 ? meta.translations : null;
            }

            if (Object.keys(metaChanges).length > 0) {
                payload.meta = metaChanges;
            }

            // Check schema changes (only if field has schema)
            if (field?.schema) {
                const schemaChanges: Record<string, unknown> = {};
                if (schema.is_nullable !== originalValues.schema.is_nullable) schemaChanges.is_nullable = schema.is_nullable;
                if (schema.is_unique !== originalValues.schema.is_unique) schemaChanges.is_unique = schema.is_unique;
                if (schema.is_indexed !== originalValues.schema.is_indexed) schemaChanges.is_indexed = schema.is_indexed;
                if (schema.default_value !== originalValues.schema.default_value) {
                    schemaChanges.default_value = schema.default_value || null;
                }
                if (schema.max_length !== originalValues.schema.max_length) schemaChanges.max_length = schema.max_length;

                if (Object.keys(schemaChanges).length > 0) {
                    payload.schema = schemaChanges;
                }
            }
        }

        return payload;
    };

    const handleSave = async () => {
        const payload = buildChangedPayload();
        const hasFieldChanges = Object.keys(payload).length > 0;
        const hasM2ORelationChanges = isM2OField && relation && onDeleteAction !== originalOnDelete;
        const hasM2MRelationChanges = isM2MField && m2mRelationData.data && (
            m2mOnDeselectAction !== originalM2mOnDeselect ||
            m2mOnDeleteCurrent !== originalM2mOnDeleteCurrent ||
            m2mOnDeleteRelated !== originalM2mOnDeleteRelated
        );
        const hasRelationChanges = hasM2ORelationChanges || hasM2MRelationChanges;

        // Don't make request if nothing changed
        if (!hasFieldChanges && !hasRelationChanges) {
            toast.info('No changes to save');
            return;
        }

        try {
            // Save field changes
            if (hasFieldChanges) {
                await updateField.mutateAsync({
                    field: fieldName,
                    payload,
                });
            }

            // Save M2O relation changes (on_delete action)
            if (hasM2ORelationChanges) {
                await updateRelation.mutateAsync({
                    schema: {
                        on_delete: onDeleteAction as 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT',
                    },
                });
            }

            // Save M2M relation changes
            if (hasM2MRelationChanges && m2mRelationData.junctionCollection && m2mRelationData.junctionFieldCurrent) {
                // Update the current side relation (junction -> current collection)
                if (m2mOnDeselectAction !== originalM2mOnDeselect || m2mOnDeleteCurrent !== originalM2mOnDeleteCurrent) {
                    try {
                        const { updateRelation: updateM2MRelation } = await import('@/api/relations');
                        await updateM2MRelation(
                            m2mRelationData.junctionCollection,
                            m2mRelationData.junctionFieldCurrent,
                            {
                                schema: {
                                    on_delete: m2mOnDeleteCurrent as 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT',
                                },
                                meta: {
                                    one_deselect_action: m2mOnDeselectAction as 'nullify' | 'delete',
                                },
                            }
                        );
                    } catch (e) {
                        console.error('Failed to update M2M current side relation:', e);
                    }
                }

                // Update the related side relation (junction -> related collection)
                if (m2mOnDeleteRelated !== originalM2mOnDeleteRelated && m2mRelationData.junctionFieldRelated) {
                    try {
                        const { updateRelation: updateM2MRelation } = await import('@/api/relations');
                        await updateM2MRelation(
                            m2mRelationData.junctionCollection,
                            m2mRelationData.junctionFieldRelated,
                            {
                                schema: {
                                    on_delete: m2mOnDeleteRelated as 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT',
                                },
                            }
                        );
                    } catch (e) {
                        console.error('Failed to update M2M related side relation:', e);
                    }
                }

                // Update original values
                setOriginalM2mOnDeselect(m2mOnDeselectAction);
                setOriginalM2mOnDeleteCurrent(m2mOnDeleteCurrent);
                setOriginalM2mOnDeleteRelated(m2mOnDeleteRelated);
            }

            toast.success('Field updated successfully');
            setIsDirty(false);
            // Update original values to current
            setOriginalValues({ meta, schema });
            setOriginalOnDelete(onDeleteAction);
        } catch (error) {
            toast.error(`Failed to update field: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleDelete = () => {
        deleteField.mutate(fieldName, {
            onSuccess: () => {
                toast.success('Field deleted successfully');
                onOpenChange(false);
            },
            onError: (error: Error) => {
                toast.error(`Failed to delete field: ${error.message}`);
            },
        });
    };

    const isPrimaryKey = field?.schema?.is_primary_key;
    const isAlias = !field?.schema;

    // Check if there are any unsaved changes (field or relation)
    const hasM2ORelationChanges = isM2OField && relation && onDeleteAction !== originalOnDelete;
    const hasM2MRelationChanges = isM2MField && m2mRelationData.data && (
        m2mOnDeselectAction !== originalM2mOnDeselect ||
        m2mOnDeleteCurrent !== originalM2mOnDeleteCurrent ||
        m2mOnDeleteRelated !== originalM2mOnDeleteRelated
    );
    const hasUnsavedChanges = isDirty || hasM2ORelationChanges || hasM2MRelationChanges;
    const isSaving = updateField.isPending || updateRelation.isPending;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-[650px] sm:max-w-[650px] flex flex-col h-full p-0">
                <SheetHeader className="p-6 pb-0">
                    <SheetTitle className="font-mono">{fieldName}</SheetTitle>
                    <SheetDescription>
                        {fieldType} field in {collection}
                    </SheetDescription>
                </SheetHeader>

                {isLoading ? (
                    <div className="flex-1 p-6 space-y-4">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto px-6 py-6">
                        <Tabs value={currentTab} onValueChange={setCurrentTab}>
                            <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
                                <TabsTrigger value="schema">Schema</TabsTrigger>
                                <TabsTrigger value="field">Field</TabsTrigger>
                                <TabsTrigger value="interface">Interface</TabsTrigger>
                                {isRelationalField && (
                                    <TabsTrigger value="relationship">Relationship</TabsTrigger>
                                )}
                                <TabsTrigger value="display" disabled>
                                    Display
                                    <span className="ml-1 text-[10px] text-muted-foreground">(Soon)</span>
                                </TabsTrigger>
                                <TabsTrigger value="validation" disabled>
                                    Validation
                                    <span className="ml-1 text-[10px] text-muted-foreground">(Soon)</span>
                                </TabsTrigger>
                                <TabsTrigger value="conditions" disabled>
                                    Conditions
                                    <span className="ml-1 text-[10px] text-muted-foreground">(Soon)</span>
                                </TabsTrigger>
                            </TabsList>

                            {/* Schema Tab */}
                            <TabsContent value="schema" className="mt-6 space-y-6">
                                {/* Field Key (readonly) */}
                                <div className="space-y-2">
                                    <Label>Key</Label>
                                    <Input value={fieldName} disabled className="font-mono bg-muted" />
                                </div>

                                {/* Type - always show, but as read-only text for alias types */}
                                <div className="space-y-2">
                                    <Label>Type</Label>
                                    {isAlias ? (
                                        <div className="flex items-center gap-2">
                                            <Input
                                                value={isM2MField ? 'alias (Many to Many)' : isM2OField ? 'alias (Many to One)' : 'alias'}
                                                disabled
                                                className="font-mono bg-muted"
                                            />
                                        </div>
                                    ) : (
                                        <Select value={fieldType} disabled>
                                            <SelectTrigger className="bg-muted">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TYPE_OPTIONS.map((type) => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>

                                {/* Default Value */}
                                {!isAlias && !isPrimaryKey && (
                                    <div className="space-y-2">
                                        <Label>Default Value</Label>
                                        {fieldType === 'boolean' ? (
                                            <div className="flex items-center space-x-2">
                                                <Switch
                                                    checked={schema.default_value === 'true'}
                                                    onCheckedChange={(checked) => updateSchemaField('default_value', checked ? 'true' : 'false')}
                                                />
                                                <span className="text-sm text-muted-foreground">
                                                    {schema.default_value === 'true' ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </div>
                                        ) : (
                                            <Input
                                                value={schema.default_value}
                                                onChange={(e) => updateSchemaField('default_value', e.target.value)}
                                                placeholder="NULL"
                                                className="font-mono"
                                            />
                                        )}
                                    </div>
                                )}

                                {/* Constraints */}
                                {!isAlias && !isPrimaryKey && (
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <Label>Nullable</Label>
                                            <Switch
                                                checked={schema.is_nullable}
                                                onCheckedChange={(checked) => updateSchemaField('is_nullable', checked)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <Label>Unique</Label>
                                            <Switch
                                                checked={schema.is_unique}
                                                onCheckedChange={(checked) => updateSchemaField('is_unique', checked)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <Label>Indexed</Label>
                                            <Switch
                                                checked={schema.is_indexed}
                                                onCheckedChange={(checked) => updateSchemaField('is_indexed', checked)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Triggers - On Create / On Update */}
                                {!isAlias && !isPrimaryKey && ['uuid', 'timestamp', 'datetime', 'date', 'time'].includes(fieldType) && (
                                    <>
                                        <div className="border-t border-border pt-6">
                                            <h3 className="font-medium mb-4">Triggers</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>On Create</Label>
                                                    <Select value={getOnCreateSpecial()} onValueChange={setOnCreateSpecial}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Do nothing" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(meta.interface === 'datetime' ? DATETIME_ON_CREATE_OPTIONS : ON_CREATE_OPTIONS).map((opt) => (
                                                                <SelectItem key={opt.value || 'none'} value={opt.value}>
                                                                    {opt.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>On Update</Label>
                                                    <Select value={getOnUpdateSpecial()} onValueChange={setOnUpdateSpecial}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Do nothing" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(meta.interface === 'datetime' ? DATETIME_ON_UPDATE_OPTIONS : ON_UPDATE_OPTIONS).map((opt) => (
                                                                <SelectItem key={opt.value || 'none'} value={opt.value}>
                                                                    {opt.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {isPrimaryKey && (
                                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4" />
                                            <span>This is the primary key field. Schema options cannot be changed.</span>
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            {/* Field Tab */}
                            <TabsContent value="field" className="mt-6 space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                        <div>
                                            <Label>Readonly</Label>
                                            <p className="text-xs text-muted-foreground">Prevent editing</p>
                                        </div>
                                        <Switch
                                            checked={meta.readonly}
                                            onCheckedChange={(checked) => updateMetaField('readonly', checked)}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                        <div>
                                            <Label>Required</Label>
                                            <p className="text-xs text-muted-foreground">Value must be set</p>
                                        </div>
                                        <Switch
                                            checked={meta.required}
                                            onCheckedChange={(checked) => updateMetaField('required', checked)}
                                        />
                                    </div>
                                </div>

                                {meta.readonly && meta.required && (
                                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4" />
                                            <span>A field that is both readonly and required may cause issues when creating items.</span>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                    <div>
                                        <Label>Hidden</Label>
                                        <p className="text-xs text-muted-foreground">Hide from detail form</p>
                                    </div>
                                    <Switch
                                        checked={meta.hidden}
                                        onCheckedChange={(checked) => updateMetaField('hidden', checked)}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Note</Label>
                                    <Textarea
                                        value={meta.note}
                                        onChange={(e) => updateMetaField('note', e.target.value)}
                                        placeholder="Add a note to help editors understand this field..."
                                        rows={3}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        A helpful note shown below the field input
                                    </p>
                                </div>

                                {/* Field Name Translation */}
                                <div className="space-y-2">
                                    <Label>Display Name</Label>
                                    <Input
                                        value={meta.translations?.find(t => t.language === 'en-US')?.translation ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const currentTranslations = meta.translations || [];
                                            const existingIndex = currentTranslations.findIndex(t => t.language === 'en-US');

                                            let newTranslations: FieldTranslation[];
                                            if (value.trim() === '') {
                                                // Remove translation if empty
                                                newTranslations = currentTranslations.filter(t => t.language !== 'en-US');
                                            } else if (existingIndex >= 0) {
                                                // Update existing translation
                                                newTranslations = [...currentTranslations];
                                                newTranslations[existingIndex] = { language: 'en-US', translation: value };
                                            } else {
                                                // Add new translation
                                                newTranslations = [...currentTranslations, { language: 'en-US', translation: value }];
                                            }

                                            updateMetaField('translations', newTranslations);
                                        }}
                                        placeholder={fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Custom display name for this field. Leave empty to use the auto-formatted field name.
                                    </p>
                                </div>
                            </TabsContent>

                            {/* Interface Tab */}
                            <TabsContent value="interface" className="mt-6 space-y-6">
                                {/* Interface selector - hidden for datetime, limited for markdown */}
                                {meta.interface !== 'datetime' && (
                                    <div className="space-y-2">
                                        <Label>Interface</Label>
                                        <Select
                                            value={meta.interface}
                                            onValueChange={(v) => updateMetaField('interface', v)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(meta.interface === 'input-rich-text-md' ? MARKDOWN_ALLOWED_INTERFACES : INTERFACES).map((iface) => (
                                                    <SelectItem key={iface.value} value={iface.value}>
                                                        {iface.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            The interface determines how the field is displayed
                                        </p>
                                    </div>
                                )}

                                {/* Width - hidden for datetime and markdown */}
                                {meta.interface !== 'datetime' && meta.interface !== 'input-rich-text-md' && (
                                    <div className="space-y-2">
                                        <Label>Width</Label>
                                        <Select
                                            value={meta.width}
                                            onValueChange={(v) => updateMetaField('width', v as WidthType)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {WIDTH_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            Control how much horizontal space this field takes
                                        </p>
                                    </div>
                                )}

                                {/* Interface-specific options */}

                                {/* Icon and Placeholder for dropdown */}
                                {(meta.interface === 'select-dropdown' || meta.interface === 'select-multiple-dropdown') && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Icon</Label>
                                            <Input
                                                value={(meta.options?.icon as string) || ''}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, icon: e.target.value })}
                                                placeholder="arrow_drop_down_circle"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Placeholder</Label>
                                            <Input
                                                value={(meta.options?.placeholder as string) || ''}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, placeholder: e.target.value })}
                                                placeholder="Select an option..."
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Choices for dropdown/radio interfaces */}
                                {(meta.interface === 'select-dropdown' || meta.interface === 'select-radio' || meta.interface === 'select-multiple-dropdown') && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <Label>Choices</Label>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={exportChoicesToCSV}
                                                    disabled={!meta.options?.choices || (meta.options.choices as Array<{ text: string; value: string }>).length === 0}
                                                    title="Export choices to CSV"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => document.getElementById('import-choices-csv-detail')?.click()}
                                                    title="Import choices from CSV"
                                                >
                                                    <Upload className="h-4 w-4" />
                                                </Button>
                                                <input
                                                    id="import-choices-csv-detail"
                                                    type="file"
                                                    accept=".csv"
                                                    onChange={importChoicesFromCSV}
                                                    className="hidden"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            {(meta.options?.choices as Array<{ text: string; value: string }> || []).map((choice, index) => (
                                                <div key={index} className="flex gap-2">
                                                    <Input
                                                        value={choice.text}
                                                        onChange={(e) => {
                                                            const newChoices = [...(meta.options?.choices as Array<{ text: string; value: string }> || [])];
                                                            const current = newChoices[index];
                                                            if (current) {
                                                                newChoices[index] = { text: e.target.value, value: current.value };
                                                                updateMetaField('options', { ...meta.options, choices: newChoices });
                                                            }
                                                        }}
                                                        placeholder="Display text"
                                                        className="flex-1"
                                                    />
                                                    <Input
                                                        value={choice.value}
                                                        onChange={(e) => {
                                                            const newChoices = [...(meta.options?.choices as Array<{ text: string; value: string }> || [])];
                                                            const current = newChoices[index];
                                                            if (current) {
                                                                newChoices[index] = { text: current.text, value: e.target.value };
                                                                updateMetaField('options', { ...meta.options, choices: newChoices });
                                                            }
                                                        }}
                                                        placeholder="Value"
                                                        className="flex-1 font-mono"
                                                    />
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            const newChoices = (meta.options?.choices as Array<{ text: string; value: string }> || []).filter((_, i) => i !== index);
                                                            updateMetaField('options', { ...meta.options, choices: newChoices });
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                const currentChoices = (meta.options?.choices as Array<{ text: string; value: string }>) || [];
                                                updateMetaField('options', { ...meta.options, choices: [...currentChoices, { text: '', value: '' }] });
                                            }}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Choice
                                        </Button>
                                    </div>
                                )}

                                {/* Placeholder for input interfaces */}
                                {(meta.interface === 'input' || meta.interface === 'input-url' || meta.interface === 'input-multiline') && (
                                    <div className="space-y-2">
                                        <Label>Placeholder</Label>
                                        <Input
                                            value={(meta.options?.placeholder as string) || ''}
                                            onChange={(e) => updateMetaField('options', { ...meta.options, placeholder: e.target.value })}
                                            placeholder="Enter placeholder text..."
                                        />
                                    </div>
                                )}

                                {/* Icons for input interface */}
                                {(meta.interface === 'input' || meta.interface === 'input-url') && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Icon Left</Label>
                                            <Input
                                                value={(meta.options?.iconLeft as string) || ''}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, iconLeft: e.target.value })}
                                                placeholder="search"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Icon Right</Label>
                                            <Input
                                                value={(meta.options?.iconRight as string) || ''}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, iconRight: e.target.value })}
                                                placeholder="close"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Allow Other / Allow None for dropdowns */}
                                {(meta.interface === 'select-dropdown' || meta.interface === 'select-multiple-dropdown') && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <Label>Allow Other</Label>
                                            <Switch
                                                checked={(meta.options?.allowOther as boolean) || false}
                                                onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, allowOther: checked })}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                            <Label>Allow None</Label>
                                            <Switch
                                                checked={(meta.options?.allowNone as boolean) || false}
                                                onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, allowNone: checked })}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Advanced Options for Input interface */}
                                {meta.interface === 'input' && (
                                    <div className="space-y-4 rounded-lg border border-border p-4">
                                        <h4 className="font-medium text-sm text-muted-foreground">Advanced Options</h4>

                                        {/* Soft Length and Font */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Soft Length</Label>
                                                <Input
                                                    type="number"
                                                    value={(meta.options?.softLength as number) || ''}
                                                    onChange={(e) => updateMetaField('options', {
                                                        ...meta.options,
                                                        softLength: e.target.value ? parseInt(e.target.value) : null
                                                    })}
                                                    placeholder="255"
                                                    min={1}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Font</Label>
                                                <Select
                                                    value={(meta.options?.font as string) || 'sans-serif'}
                                                    onValueChange={(v) => updateMetaField('options', { ...meta.options, font: v })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="sans-serif">Sans Serif</SelectItem>
                                                        <SelectItem value="monospace">Monospace</SelectItem>
                                                        <SelectItem value="serif">Serif</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        {/* Toggle options - Trim, Masked, Clear, Slug */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Trim</Label>
                                                    <p className="text-xs text-muted-foreground">Trim whitespace</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.trim as boolean) || false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, trim: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Masked</Label>
                                                    <p className="text-xs text-muted-foreground">Hide characters</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.masked as boolean) || false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, masked: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Clear</Label>
                                                    <p className="text-xs text-muted-foreground">Save as empty string</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.clear as boolean) || false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, clear: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Slug</Label>
                                                    <p className="text-xs text-muted-foreground">Convert to URL slug</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.slug as boolean) || false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, slug: checked })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Advanced Options for Dropdown interface */}
                                {(meta.interface === 'select-dropdown' || meta.interface === 'select-multiple-dropdown') && (
                                    <div className="space-y-4 rounded-lg border border-border p-4">
                                        <h4 className="font-medium text-sm text-muted-foreground">Advanced Options</h4>

                                        {/* Soft Length and Font */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Soft Length</Label>
                                                <Input
                                                    type="number"
                                                    value={(meta.options?.softLength as number) || ''}
                                                    onChange={(e) => updateMetaField('options', {
                                                        ...meta.options,
                                                        softLength: e.target.value ? parseInt(e.target.value) : null
                                                    })}
                                                    placeholder="255"
                                                    min={1}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Font</Label>
                                                <Select
                                                    value={(meta.options?.font as string) || 'sans-serif'}
                                                    onValueChange={(v) => updateMetaField('options', { ...meta.options, font: v })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="sans-serif">Sans Serif</SelectItem>
                                                        <SelectItem value="monospace">Monospace</SelectItem>
                                                        <SelectItem value="serif">Serif</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        {/* Toggle options - Trim, Clear */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Trim</Label>
                                                    <p className="text-xs text-muted-foreground">Trim whitespace</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.trim as boolean) || false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, trim: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Clear</Label>
                                                    <p className="text-xs text-muted-foreground">Save as empty string</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.clear as boolean) || false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, clear: checked })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* M2O Interface Options */}
                                {meta.interface === 'select-dropdown-m2o' && (
                                    <>
                                        {/* Display Template */}
                                        <div className="space-y-2">
                                            <Label>Display Template</Label>
                                            {relation?.related_collection ? (
                                                <DisplayTemplateInput
                                                    value={(meta.options?.template as string) || ''}
                                                    onChange={(value) => updateMetaField('options', { ...meta.options, template: value })}
                                                    collection={relation.related_collection}
                                                    placeholder="{{title}} - {{id}}"
                                                />
                                            ) : (
                                                <Input
                                                    value={(meta.options?.template as string) || ''}
                                                    onChange={(e) => updateMetaField('options', { ...meta.options, template: e.target.value })}
                                                    placeholder="{{title}} - {{id}}"
                                                />
                                            )}
                                        </div>

                                        {/* Filter - JSON */}
                                        <div className="space-y-2">
                                            <Label>Filter</Label>
                                            <Textarea
                                                value={typeof meta.options?.filter === 'object'
                                                    ? JSON.stringify(meta.options.filter, null, 2)
                                                    : (meta.options?.filter as string) || ''}
                                                onChange={(e) => {
                                                    try {
                                                        const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                                                        updateMetaField('options', { ...meta.options, filter: parsed });
                                                    } catch {
                                                        // Keep as string if not valid JSON
                                                        updateMetaField('options', { ...meta.options, filter: e.target.value });
                                                    }
                                                }}
                                                placeholder='{"status": {"_eq": "published"}}'
                                                className="font-mono text-sm"
                                                rows={3}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Filter to limit selectable items (JSON format)
                                            </p>
                                        </div>

                                        {/* Toggle options - 2x2 grid */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Enable Create</Label>
                                                    <p className="text-xs text-muted-foreground">Allow creating new items</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableCreate as boolean) ?? true}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableCreate: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Enable Select</Label>
                                                    <p className="text-xs text-muted-foreground">Allow selecting items</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableSelect as boolean) ?? true}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableSelect: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Enable Link</Label>
                                                    <p className="text-xs text-muted-foreground">Show link to related item</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableLink as boolean) ?? false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableLink: checked })}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* M2M Interface Options */}
                                {meta.interface === 'list-m2m' && (
                                    <>
                                        {/* Layout */}
                                        <div className="space-y-2">
                                            <Label>Layout</Label>
                                            <Select
                                                value={(meta.options?.layout as string) || 'list'}
                                                onValueChange={(v) => updateMetaField('options', { ...meta.options, layout: v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="list">List</SelectItem>
                                                    <SelectItem value="table">Table</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                How related items are displayed
                                            </p>
                                        </div>

                                        {/* Display Template */}
                                        <div className="space-y-2">
                                            <Label>Display Template</Label>
                                            {m2mRelationData?.junctionCollection ? (
                                                <DisplayTemplateInput
                                                    value={(meta.options?.template as string) || ''}
                                                    onChange={(value) => updateMetaField('options', { ...meta.options, template: value })}
                                                    collection={m2mRelationData.junctionCollection}
                                                    placeholder="{{id}} {{related_field.name}}"
                                                />
                                            ) : (
                                                <Input
                                                    value={(meta.options?.template as string) || ''}
                                                    onChange={(e) => updateMetaField('options', { ...meta.options, template: e.target.value })}
                                                    placeholder="{{id}} {{related_field.name}}"
                                                />
                                            )}
                                        </div>

                                        {/* Per Page Limit */}
                                        <div className="space-y-2">
                                            <Label>Per Page</Label>
                                            <Input
                                                type="number"
                                                value={(meta.options?.limit as number) || 15}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, limit: parseInt(e.target.value) || 15 })}
                                                min={1}
                                                max={100}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Number of items to show per page
                                            </p>
                                        </div>

                                        {/* Junction Field Location */}
                                        <div className="space-y-2">
                                            <Label>Junction Field Location</Label>
                                            <Select
                                                value={(meta.options?.junctionFieldLocation as string) || 'bottom'}
                                                onValueChange={(v) => updateMetaField('options', { ...meta.options, junctionFieldLocation: v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="top">Top</SelectItem>
                                                    <SelectItem value="bottom">Bottom</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                Where to display junction-specific fields
                                            </p>
                                        </div>

                                        {/* Toggle options - Grid */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Creating Items</Label>
                                                    <p className="text-xs text-muted-foreground">Enable create button</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableCreate as boolean) ?? true}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableCreate: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Selecting Items</Label>
                                                    <p className="text-xs text-muted-foreground">Enable select button</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableSelect as boolean) ?? true}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableSelect: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Allow Duplicates</Label>
                                                    <p className="text-xs text-muted-foreground">Allow linking same item multiple times</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.allowDuplicates as boolean) ?? false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, allowDuplicates: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Item Link</Label>
                                                    <p className="text-xs text-muted-foreground">Show link to edit items</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableLink as boolean) ?? false}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableLink: checked })}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Markdown Interface Options */}
                                {meta.interface === 'input-rich-text-md' && (
                                    <>
                                        {/* Toolbar Options */}
                                        <div className="space-y-3">
                                            <Label>Toolbar Options</Label>
                                            <div className="flex flex-wrap gap-2">
                                                {MARKDOWN_TOOLBAR_OPTIONS.map((opt) => {
                                                    const toolbar = (meta.options?.toolbar as string[]) || ['heading', 'bold', 'italic', 'strikethrough', 'bullist', 'numlist', 'blockquote', 'code', 'link', 'table', 'image'];
                                                    const isSelected = toolbar.includes(opt.value);
                                                    return (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            onClick={() => {
                                                                const newToolbar = isSelected
                                                                    ? toolbar.filter(t => t !== opt.value)
                                                                    : [...toolbar, opt.value];
                                                                updateMetaField('options', { ...meta.options, toolbar: newToolbar });
                                                            }}
                                                            className={cn(
                                                                'px-3 py-1.5 text-sm rounded-md border transition-colors',
                                                                isSelected
                                                                    ? 'bg-primary text-primary-foreground border-primary'
                                                                    : 'bg-background border-border hover:bg-accent'
                                                            )}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Click to toggle toolbar buttons
                                            </p>
                                        </div>

                                        {/* Placeholder */}
                                        <div className="space-y-2">
                                            <Label>Placeholder</Label>
                                            <Input
                                                value={(meta.options?.placeholder as string) || ''}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, placeholder: e.target.value })}
                                                placeholder="Enter placeholder text..."
                                            />
                                        </div>

                                        {/* Soft Length, Editor Font, Preview Font */}
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label>Soft Length</Label>
                                                <Input
                                                    type="number"
                                                    value={(meta.options?.softLength as number) || ''}
                                                    onChange={(e) => updateMetaField('options', {
                                                        ...meta.options,
                                                        softLength: e.target.value ? parseInt(e.target.value) : null
                                                    })}
                                                    placeholder="No limit"
                                                    min={1}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Editor Font</Label>
                                                <Select
                                                    value={(meta.options?.editorFont as string) || 'sans-serif'}
                                                    onValueChange={(v) => updateMetaField('options', { ...meta.options, editorFont: v })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="sans-serif">Sans Serif</SelectItem>
                                                        <SelectItem value="monospace">Monospace</SelectItem>
                                                        <SelectItem value="serif">Serif</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Preview Font</Label>
                                                <Select
                                                    value={(meta.options?.previewFont as string) || 'sans-serif'}
                                                    onValueChange={(v) => updateMetaField('options', { ...meta.options, previewFont: v })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="sans-serif">Sans Serif</SelectItem>
                                                        <SelectItem value="monospace">Monospace</SelectItem>
                                                        <SelectItem value="serif">Serif</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        {/* Default View */}
                                        <div className="space-y-2">
                                            <Label>Default View</Label>
                                            <Select
                                                value={(meta.options?.defaultView as string) || 'editor'}
                                                onValueChange={(v) => updateMetaField('options', { ...meta.options, defaultView: v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="editor">Editor</SelectItem>
                                                    <SelectItem value="preview">Preview</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                Choose which view to show by default
                                            </p>
                                        </div>
                                    </>
                                )}

                                {/* DateTime Interface Options */}
                                {meta.interface === 'datetime' && (
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Format</Label>
                                            <Select
                                                value={(meta.options?.format as string) || 'long'}
                                                onValueChange={(v) => updateMetaField('options', { ...meta.options, format: v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="long">Long</SelectItem>
                                                    <SelectItem value="short">Short</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                Choose the display format for the date/time
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Custom Format</Label>
                                            <Input
                                                value={(meta.options?.format as string) !== 'long' && (meta.options?.format as string) !== 'short' ? (meta.options?.format as string) : ''}
                                                onChange={(e) => updateMetaField('options', { ...meta.options, format: e.target.value || 'long' })}
                                                placeholder="YYYY-MM-DD HH:mm:ss"
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Enter a custom date format (e.g., YYYY-MM-DD HH:mm)
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* File/Image Interface Options */}
                                {(meta.interface === 'file' || meta.interface === 'file-image') && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Enable Create</Label>
                                                    <p className="text-xs text-muted-foreground">Allow uploading files</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableCreate as boolean) ?? true}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableCreate: checked })}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                                <div>
                                                    <Label>Enable Select</Label>
                                                    <p className="text-xs text-muted-foreground">Select from library</p>
                                                </div>
                                                <Switch
                                                    checked={(meta.options?.enableSelect as boolean) ?? true}
                                                    onCheckedChange={(checked) => updateMetaField('options', { ...meta.options, enableSelect: checked })}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Default Folder</Label>
                                            <FolderSelect
                                                value={(meta.options?.folder as string | null) ?? null}
                                                onChange={(folderId) => {
                                                    const nextOptions = { ...meta.options, folder: folderId ?? null };
                                                    updateMetaField('options', nextOptions);
                                                }}
                                                placeholder="Choose a folder (Root for none)"
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Uploads from this field will be saved to the selected folder.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Button Links Interface Options */}
                                {meta.interface === 'presentation-links' && (
                                    <div className="space-y-3">
                                        <Label>Button Links</Label>
                                        <div className="space-y-3">
                                            {((meta.options?.links as LinkItem[]) || [{ label: '', icon: '', type: 'normal', url: '' }]).map((link, index) => (
                                                <div key={index} className="rounded-lg border border-border p-3 space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-muted-foreground">Link {index + 1}</span>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                const links = (meta.options?.links as LinkItem[]) || [];
                                                                updateMetaField('options', { ...meta.options, links: links.filter((_, i) => i !== index) });
                                                            }}
                                                            disabled={((meta.options?.links as LinkItem[]) || []).length <= 1}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <Label className="text-xs">Label</Label>
                                                            <Input
                                                                value={link.label}
                                                                onChange={(e) => {
                                                                    const links = [...((meta.options?.links as LinkItem[]) || [])];
                                                                    const current = links[index];
                                                                    if (current) {
                                                                        links[index] = { ...current, label: e.target.value };
                                                                        updateMetaField('options', { ...meta.options, links });
                                                                    }
                                                                }}
                                                                placeholder="Button Label"
                                                                className="h-8 text-sm"
                                                            />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <Label className="text-xs">Icon</Label>
                                                            <Input
                                                                value={link.icon}
                                                                onChange={(e) => {
                                                                    const links = [...((meta.options?.links as LinkItem[]) || [])];
                                                                    const current = links[index];
                                                                    if (current) {
                                                                        links[index] = { ...current, icon: e.target.value };
                                                                        updateMetaField('options', { ...meta.options, links });
                                                                    }
                                                                }}
                                                                placeholder="open_in_new"
                                                                className="h-8 text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <Label className="text-xs">Type</Label>
                                                            <Select
                                                                value={link.type}
                                                                onValueChange={(v) => {
                                                                    const links = [...((meta.options?.links as LinkItem[]) || [])];
                                                                    const current = links[index];
                                                                    if (current) {
                                                                        links[index] = { ...current, type: v };
                                                                        updateMetaField('options', { ...meta.options, links });
                                                                    }
                                                                }}
                                                            >
                                                                <SelectTrigger className="h-8 text-sm">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {BUTTON_TYPE_OPTIONS.map((opt) => (
                                                                        <SelectItem key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <Label className="text-xs flex items-center gap-1">
                                                                URL <ExternalLink className="h-3 w-3" />
                                                            </Label>
                                                            <Input
                                                                value={link.url}
                                                                onChange={(e) => {
                                                                    const links = [...((meta.options?.links as LinkItem[]) || [])];
                                                                    const current = links[index];
                                                                    if (current) {
                                                                        links[index] = { ...current, url: e.target.value };
                                                                        updateMetaField('options', { ...meta.options, links });
                                                                    }
                                                                }}
                                                                placeholder="https://example.com/{{id}}"
                                                                className="h-8 text-sm font-mono"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                const links = (meta.options?.links as LinkItem[]) || [];
                                                updateMetaField('options', { ...meta.options, links: [...links, { label: '', icon: '', type: 'normal', url: '' }] });
                                            }}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Link
                                        </Button>
                                        <p className="text-xs text-muted-foreground">
                                            Use {'{{field}}'} syntax for dynamic values in URLs
                                        </p>
                                    </div>
                                )}
                            </TabsContent>

                            {/* Relationship Tab - For M2O and M2M fields */}
                            {isRelationalField && (
                                <TabsContent value="relationship" className="mt-6 space-y-6">
                                    {relationLoading ? (
                                        <div className="space-y-4">
                                            <Skeleton className="h-24 w-full" />
                                            <Skeleton className="h-32 w-full" />
                                        </div>
                                    ) : isM2OField ? (
                                        <>
                                            {/* M2O Relationship Visualization */}
                                            <div className="space-y-4">
                                                <Label className="text-base font-medium">Many to One Relationship</Label>

                                                {/* Visual connection grid */}
                                                <div className="rounded-lg border border-border p-4">
                                                    <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                                                        {/* This Collection (Many side) */}
                                                        <div className="space-y-2 text-center">
                                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">This Collection</p>
                                                            <div className="rounded-md bg-muted p-3">
                                                                <p className="font-mono text-sm font-medium">{collection}</p>
                                                            </div>
                                                            <div className="rounded-md border border-border p-2 bg-background">
                                                                <p className="font-mono text-xs text-muted-foreground">{fieldName}</p>
                                                            </div>
                                                        </div>

                                                        {/* Arrow */}
                                                        <div className="flex flex-col items-center gap-1">
                                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                                <span className="text-xs">Many</span>
                                                                <ArrowRight className="h-4 w-4" />
                                                                <span className="text-xs">One</span>
                                                            </div>
                                                            <Link2 className="h-4 w-4 text-primary" />
                                                        </div>

                                                        {/* Related Collection (One side) */}
                                                        <div className="space-y-2 text-center">
                                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Related Collection</p>
                                                            <div className="rounded-md bg-primary/10 p-3 border border-primary/20">
                                                                <p className="font-mono text-sm font-medium text-primary">
                                                                    {relation?.related_collection || 'Not set'}
                                                                </p>
                                                            </div>
                                                            <div className="rounded-md border border-border p-2 bg-background">
                                                                <p className="font-mono text-xs text-muted-foreground">
                                                                    {relation?.schema?.foreign_key_column || 'id'} (PK)
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Relationship info */}
                                                <div className="text-sm text-muted-foreground">
                                                    <p>
                                                        Many items in <span className="font-mono text-foreground">{collection}</span> can link to
                                                        one item in <span className="font-mono text-foreground">{relation?.related_collection || '...'}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Relational Triggers */}
                                            <div className="space-y-4 rounded-lg border border-border p-4">
                                                <h4 className="font-medium text-sm">Relational Triggers</h4>
                                                <p className="text-xs text-muted-foreground">
                                                    What happens when a related item in "{relation?.related_collection}" is deleted?
                                                </p>

                                                <div className="space-y-2">
                                                    <Label>On Delete of Related Item</Label>
                                                    <Select
                                                        value={onDeleteAction}
                                                        onValueChange={setOnDeleteAction}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {ON_DELETE_ACTIONS.map((action) => (
                                                                <SelectItem key={action.value} value={action.value}>
                                                                    <div className="flex flex-col">
                                                                        <span>{action.label}</span>
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <p className="text-xs text-muted-foreground">
                                                        {ON_DELETE_ACTIONS.find(a => a.value === onDeleteAction)?.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </>
                                    ) : isM2MField ? (
                                        <>
                                            {/* M2M Relationship Visualization */}
                                            <div className="space-y-4">
                                                <Label className="text-base font-medium">Many to Many Relationship</Label>

                                                {m2mRelationData.isLoading ? (
                                                    <div className="space-y-2">
                                                        <Skeleton className="h-32 w-full" />
                                                    </div>
                                                ) : (
                                                    <>
                                                        {/* Visual connection grid with junction */}
                                                        <div className="rounded-lg border border-border p-4">
                                                            <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-center">
                                                                {/* This Collection (Many side) */}
                                                                <div className="space-y-2 text-center">
                                                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">This Collection</p>
                                                                    <div className="rounded-md bg-muted p-3">
                                                                        <p className="font-mono text-sm font-medium">{collection}</p>
                                                                    </div>
                                                                    <div className="rounded-md border border-border p-2 bg-background">
                                                                        <p className="font-mono text-xs text-muted-foreground">{fieldName}</p>
                                                                    </div>
                                                                </div>

                                                                {/* Arrow to Junction */}
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <div className="flex items-center gap-1 text-muted-foreground">
                                                                        <span className="text-xs">1</span>
                                                                        <ArrowRight className="h-4 w-4" />
                                                                        <span className="text-xs">M</span>
                                                                    </div>
                                                                </div>

                                                                {/* Junction Collection */}
                                                                <div className="space-y-2 text-center">
                                                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Junction Collection</p>
                                                                    <div className="rounded-md bg-amber-500/10 p-3 border border-amber-500/20">
                                                                        <p className="font-mono text-sm font-medium text-amber-600 dark:text-amber-400">
                                                                            {String(m2mRelationData.junctionCollection || meta.options?.junctionCollection || 'Not set')}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-1 justify-center">
                                                                        <div className="rounded-md border border-border px-2 py-1 bg-background">
                                                                            <p className="font-mono text-[10px] text-muted-foreground">
                                                                                {String(m2mRelationData.junctionFieldCurrent || meta.options?.junctionFieldCurrent || '→ current')}
                                                                            </p>
                                                                        </div>
                                                                        <div className="rounded-md border border-border px-2 py-1 bg-background">
                                                                            <p className="font-mono text-[10px] text-muted-foreground">
                                                                                {String(m2mRelationData.junctionFieldRelated || meta.options?.junctionFieldRelated || '→ related')}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Arrow to Related */}
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <div className="flex items-center gap-1 text-muted-foreground">
                                                                        <span className="text-xs">M</span>
                                                                        <ArrowRight className="h-4 w-4" />
                                                                        <span className="text-xs">1</span>
                                                                    </div>
                                                                </div>

                                                                {/* Related Collection (Many side) */}
                                                                <div className="space-y-2 text-center">
                                                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Related Collection</p>
                                                                    <div className="rounded-md bg-primary/10 p-3 border border-primary/20">
                                                                        <p className="font-mono text-sm font-medium text-primary">
                                                                            {String(m2mRelationData.relatedCollection || meta.options?.relatedCollection || 'Not set')}
                                                                        </p>
                                                                    </div>
                                                                    <div className="rounded-md border border-border p-2 bg-background">
                                                                        <p className="font-mono text-xs text-muted-foreground">
                                                                            id (PK)
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Relationship info */}
                                                        <div className="text-sm text-muted-foreground">
                                                            <p>
                                                                Many items in <span className="font-mono text-foreground">{collection}</span> can link to
                                                                many items in <span className="font-mono text-foreground">{String(m2mRelationData.relatedCollection || meta.options?.relatedCollection || '...')}</span> through
                                                                the junction collection.
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Junction Collection Info */}
                                            <div className="space-y-4 rounded-lg border border-border p-4">
                                                <h4 className="font-medium text-sm">Junction Collection Details</h4>
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Junction Collection</Label>
                                                        <p className="font-mono mt-1">
                                                            {String(m2mRelationData.junctionCollection || meta.options?.junctionCollection || 'Auto-generated')}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Related Collection</Label>
                                                        <p className="font-mono mt-1">
                                                            {String(m2mRelationData.relatedCollection || meta.options?.relatedCollection || 'Not set')}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Field → Current</Label>
                                                        <p className="font-mono mt-1">
                                                            {String(m2mRelationData.junctionFieldCurrent || meta.options?.junctionFieldCurrent || 'Auto-generated')}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Field → Related</Label>
                                                        <p className="font-mono mt-1">
                                                            {String(m2mRelationData.junctionFieldRelated || meta.options?.junctionFieldRelated || 'Auto-generated')}
                                                        </p>
                                                    </div>
                                                </div>
                                                {Boolean(m2mRelationData.sortField || meta.options?.sortField) && (
                                                    <div>
                                                        <Label className="text-xs text-muted-foreground">Sort Field</Label>
                                                        <p className="font-mono mt-1">{String(m2mRelationData.sortField || meta.options?.sortField || '')}</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* M2M Relational Triggers */}
                                            <div className="space-y-4 rounded-lg border border-border p-4">
                                                <h4 className="font-medium text-sm">Relational Triggers</h4>
                                                <p className="text-xs text-muted-foreground">
                                                    Configure what happens when items are modified or deleted
                                                </p>

                                                <div className="space-y-4">
                                                    {/* On Deselect */}
                                                    <div className="space-y-2">
                                                        <Label>On Deselect</Label>
                                                        <Select
                                                            value={m2mOnDeselectAction}
                                                            onValueChange={(v) => {
                                                                setM2mOnDeselectAction(v);
                                                                setIsDirty(true);
                                                            }}
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="nullify">Nullify - Set junction field to null</SelectItem>
                                                                <SelectItem value="delete">Delete - Remove junction entry</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        <p className="text-xs text-muted-foreground">
                                                            What happens when a linked item is deselected from this field
                                                        </p>
                                                    </div>

                                                    {/* On Delete of Current Item */}
                                                    <div className="space-y-2">
                                                        <Label>On Delete of "{collection}" Item</Label>
                                                        <Select
                                                            value={m2mOnDeleteCurrent}
                                                            onValueChange={(v) => {
                                                                setM2mOnDeleteCurrent(v);
                                                                setIsDirty(true);
                                                            }}
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {ON_DELETE_ACTIONS.map((action) => (
                                                                    <SelectItem key={action.value} value={action.value}>
                                                                        {action.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <p className="text-xs text-muted-foreground">
                                                            What happens to junction entries when a "{collection}" item is deleted
                                                        </p>
                                                    </div>

                                                    {/* On Delete of Related Item */}
                                                    <div className="space-y-2">
                                                        <Label>On Delete of "{m2mRelationData.relatedCollection || 'related'}" Item</Label>
                                                        <Select
                                                            value={m2mOnDeleteRelated}
                                                            onValueChange={(v) => {
                                                                setM2mOnDeleteRelated(v);
                                                                setIsDirty(true);
                                                            }}
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {ON_DELETE_ACTIONS.map((action) => (
                                                                    <SelectItem key={action.value} value={action.value}>
                                                                        {action.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <p className="text-xs text-muted-foreground">
                                                            What happens to junction entries when a related item is deleted
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* M2M Interface Info */}
                                            <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                                                <p className="flex items-center gap-2">
                                                    <ExternalLink className="h-3 w-3" />
                                                    <span>
                                                        Many-to-Many relationships store connections in a separate junction table.
                                                        Changes to the junction configuration require recreating the field.
                                                    </span>
                                                </p>
                                            </div>
                                        </>
                                    ) : null}
                                </TabsContent>
                            )}

                            {/* Display Tab - Coming Soon */}
                            <TabsContent value="display" className="mt-6">
                                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                                    <AlertCircle className="h-8 w-8 mb-2" />
                                    <p>Display configuration coming soon</p>
                                </div>
                            </TabsContent>

                            {/* Validation Tab - Coming Soon */}
                            <TabsContent value="validation" className="mt-6">
                                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                                    <AlertCircle className="h-8 w-8 mb-2" />
                                    <p>Validation configuration coming soon</p>
                                </div>
                            </TabsContent>

                            {/* Conditions Tab - Coming Soon */}
                            <TabsContent value="conditions" className="mt-6">
                                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                                    <AlertCircle className="h-8 w-8 mb-2" />
                                    <p>Conditional display coming soon</p>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                )}

                {/* Actions - Sticky footer */}
                <div className="shrink-0 flex justify-between gap-2 border-t border-border bg-background p-4">
                    {!isPrimaryKey && (
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Field
                        </Button>
                    )}
                    {isPrimaryKey && <div />}

                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={!hasUnsavedChanges || isSaving}>
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2 h-4 w-4" />
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Delete Dialog */}
                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Field</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to delete the field "{fieldName}"?
                                This will permanently remove the field and all its data from the collection. This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </SheetContent>
        </Sheet>
    );
}
